import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from 'components/ui/Layout';
import { PAGE_CONTENT_WIDTH } from 'utils/uiHelpers';
import PageHeader from 'components/ui/PageHeader';
import {
  ChevronLeft,
  ChevronRight,
  Edit,
  Eye,
  Search,
  UserPlus,
  X,
  Trash2,
  Download,
  Tag,
} from 'lucide-react';
import { usePrefetchOffer } from '../../features/offer/hooks/usePrefetchOffer';
import { useURLSync } from '../../features/offer/hooks/useURLSync';

import { useOffers } from '../../features/offer/hooks/useOffer';
import { formatDateTime } from 'utils/dateFormatter';
import { EditOfferModal } from 'features/offer/components/EditOfferModal';
import { ViewOfferModal } from 'features/offer/components/ViewModal';
import {
  OfferStatus,
  discountTypeStatus,
} from 'features/offer/types/offerStatus.type';
import { DeleteOfferModal } from 'features/offer/components/DeleteOfferModal';
import { useUserProfile } from 'features/auth/hooks/useUserProfile';
import { offerServices } from 'features/offer/services/offer.service';


const Offer = () => {
  const navigate = useNavigate();
  const [filters, setFilters] = useURLSync({
    page: 1,
    limit: 10,
    search: '',
    status: '',
    discountType: '',
    // pageType: "",
    sortBy: "createdAt",
    order: 'desc',
  });

  const [searchInput, setSearchInput] = useState('');
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [selectedOffer, setSelectedOffer] = useState(null);

  const [{ data, isPending, isError, error }] = useOffers(filters);

const handleExport =
  async () => {

    try {

      const response =
        await offerServices.exportOffers(
          filters
        );

      const blob =
        new Blob(
          [response.data],
          {
            type: 'text/csv',
          }
        );

      const url =
        window.URL.createObjectURL(
          blob
        );

      const link =
        document.createElement(
          'a'
        );

      link.href = url;

      link.download =
        `offers-${Date.now()}.csv`;

      document.body.appendChild(
        link
      );

      link.click();

      document.body.removeChild(
        link
      );

      window.URL.revokeObjectURL(
        url
      );

    } catch (error) {

      console.error(
        'Offer export failed',
        error
      );

    }
};

  const { data: profileData } = useUserProfile();
    const user = profileData?.data;
 
// console.log('Current Role:', user?.role?.name);
 
const restrictedRoles = [
  "delivery_manager",
  "category_manager",
  "operations_manager",
  "travel_manager",
  "finance_manager",
];
 
const showPermissionError = restrictedRoles.includes(
  user?.role?.name
);

  const { prefetchNextPage, prefetchPrevPage } = usePrefetchOffer();

  // Prefetch adjacent pages
  useEffect(() => {
    if (data?.pagination) {
      const { currentPage, totalPages } = data.pagination;
      prefetchNextPage(filters, totalPages);
      if (currentPage > 1) {
        prefetchPrevPage(filters);
      }
    }
  }, [data, filters]);

  // Update filter and reset to page 1
  const updateFilter = (key, value) => {
    setFilters((prev) => ({
      ...prev,
      [key]: value,
      page: key === 'page' ? value : '1',
    }));
  };

  // Search with debounce
  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchInput !== filters.search) {
        updateFilter('search', searchInput);
      }
    }, 500);
    return () => clearTimeout(timer);
  }, [searchInput]);

  useEffect(() => {
    setSearchInput(filters.search);
  }, [filters.search]);

  // Sort handler
const handleSort = (column) => {
  const newOrder =
    filters.sortBy === column && filters.order === 'asc'
      ? 'desc'
      : 'asc';

  setFilters((prev) => ({
    ...prev,
    sortBy: column,
    order: newOrder,
  }));
};

const SortIcon = ({ column }) => {
  if (filters.sortBy !== column)
    return <span className="text-gray-400">↕</span>;

  return filters.order === "asc"
    ? <span>↑</span>
    : <span>↓</span>;
};

  const handleEditClick = (offer) => {
    setSelectedOffer(offer);
    setIsEditModalOpen(true);
  };

  const handleViewClick = (offer) => {
    setSelectedOffer(offer);
    setIsViewModalOpen(true);
  };

  const handleDeleteClick = (offer) => {
    setSelectedOffer(offer);
    setIsDeleteModalOpen(true);
  };

  return (
    <Layout>
      <div className="min-h-screen bg-background p-6">
        <div className={PAGE_CONTENT_WIDTH}>
          <PageHeader
            icon={Tag}
            title="Offer Management"
            subtitle="Offers and Discounts"
            actions={
              <>
                {!showPermissionError && (
                  <button
                    onClick={() => navigate("/offer/add")}
                    className="flex items-center space-x-2 px-4 py-2 text-sm bg-primary text-primary-foreground rounded-md hover:bg-primary/90 transition-colors"
                  >
                    <UserPlus size={16} />
                    <span>Add Offer</span>
                  </button>
                )}

                {!showPermissionError && (
                  <button
                    onClick={handleExport}
                    className='flex items-center space-x-2 px-4 py-2 text-sm border border-border rounded-md bg-card text-card-foreground hover:bg-accent transition-colors'
                  >
                    <Download size={16} />
                    <span>Export</span>
                  </button>
                )}
              </>
            }
          />

          {/* Filters */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3 mb-6">
            {/* Search */}
            <div className="relative lg:col-span-2">
              <Search
                className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground"
                size={16}
              />
              <input
                type="text"
                placeholder="Search by Title..."
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                className="w-full pl-10 pr-10 py-2 text-sm border border-border rounded-md bg-input text-foreground placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary"
              />
              {searchInput && (
                <button
                  onClick={() => setSearchInput("")}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-muted-foreground hover:text-foreground"
                >
                  <X size={16} />
                </button>
              )}
            </div>

            {/* Status Filter */}
            <select
              value={filters.status}
              onChange={(e) => updateFilter("status", e.target.value)}
              className="px-3 py-2 text-sm border border-border rounded-md bg-input text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
            >
              <option value="">All Status</option>
              <option value={OfferStatus.PENDING}>Pending</option>
              <option value={OfferStatus.ACTIVE}>Active</option>
              <option value={OfferStatus.EXPIRED}>Expired</option>
              <option value={OfferStatus.SCHEDULED}>Scheduled</option>
            </select>

            {/* discount Filter */}
            <select
              value={filters.discountType}
              onChange={(e) => updateFilter("discountType", e.target.value)}
              className="px-3 py-2 text-sm border border-border rounded-md bg-input text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
            >
              <option value="">Discount Type</option>
              <option value={discountTypeStatus.PERCENTAGE}>percentage</option>
              <option value={discountTypeStatus.FIXED}>fixed</option>
            </select>

            {/* Items per page */}
            <select
              value={filters.limit}
              onChange={(e) => updateFilter("limit", e.target.value)}
              className="px-3 py-2 text-sm border border-border rounded-md bg-input text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
            >
              <option value="5">5 per page</option>
              <option value="10">10 per page</option>
              <option value="20">20 per page</option>
              <option value="50">50 per page</option>
            </select>
          </div>

          {/* Cms Table */}
          <div className="bg-card border border-border rounded-lg overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-muted border-b border-border">
                  <tr>
                    <th
                      className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase cursor-pointer hover:text-foreground"
                      onClick={() => handleSort("title")}
                    >
                      <div className="flex items-center space-x-1">
                        <span>Title</span>
                        <SortIcon column="title" />
                      </div>
                    </th>
                    <th
                      className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase cursor-pointer hover:text-foreground"
                      onClick={() => handleSort("code")}
                    >
                      <div className="flex items-center space-x-1">
                        <span>Code</span>
                        <SortIcon column="code" />
                      </div>
                    </th>
                    <th
                      className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase cursor-pointer hover:text-foreground"
                      onClick={() => handleSort("discountType")}
                    >
                      <div className="flex items-center space-x-1">
                        <span>Discount Type</span>
                        <SortIcon column="discountType" />
                      </div>
                    </th>
                    <th
                      className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase cursor-pointer hover:text-foreground"
                      onClick={() => handleSort("startDate")}
                    >
                      <div className="flex items-center space-x-1">
                        <span>Start Date</span>
                        <SortIcon column="startDate" />
                      </div>
                    </th>
                    <th
                      className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase cursor-pointer hover:text-foreground"
                      onClick={() => handleSort("endDate")}
                    >
                      <div className="flex items-center space-x-1">
                        <span>End Date</span>
                        <SortIcon column="endDate" />
                      </div>
                    </th>
                    {/* <th
                      className='px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase cursor-pointer hover:text-foreground'
                      onClick={() => handleSort('conditions')}
                    >
                      <div className='flex items-center space-x-1'>
                        <span>Conditions</span>
                        <SortIcon column='conditions' />
                      </div>
                    </th> */}
                    <th
                      className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase cursor-pointer hover:text-foreground"
                      onClick={() => handleSort("status")}
                    >
                      <div className="flex items-center space-x-1">
                        <span>Status</span>
                        <SortIcon column="status" />
                      </div>
                    </th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-muted-foreground uppercase">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {isPending ? (
                    <tr>
                      <td
                        colSpan={8}
                        className="px-4 py-6 text-center text-muted-foreground"
                      >
                        Loading...
                      </td>
                    </tr>
                  ) : isError ? (
                    <tr>
                      <td
                        colSpan={8}
                        className="px-4 py-6 text-center text-destructive"
                      >
                        Error: {error.message}
                      </td>
                    </tr>
                  ) : data?.data.length === 0 ? (
                    <tr>
                      <td
                        colSpan={8}
                        className="px-4 py-6 text-center text-muted-foreground"
                      >
                        No offers found.
                      </td>
                    </tr>
                  ) : (
                    data?.data.map((offer) => (
                      <tr key={offer._id} className="hover:bg-accent">
                        <td className="px-4 py-3 text-sm text-foreground">
                          {offer.title}
                        </td>
                        <td className="px-4 py-3 text-sm text-foreground">
                          {offer.code}
                        </td>
                        <td className="px-4 py-3 text-sm text-foreground">
                          {offer.discountType}
                        </td>
                        <td className="px-4 py-3 text-sm text-foreground">
                          {formatDateTime(offer.startDate)}
                        </td>
                        <td className="px-4 py-3 text-sm text-foreground">
                          {formatDateTime(offer.endDate)}
                        </td>
                        {/* <td className='px-4 py-3 text-sm text-foreground'>
                          {offer.conditions}
                        </td> */}
                        {/* Status */}
                        <td className="px-4 py-3 text-sm">
                          <span
                            className={`inline-flex px-3 py-1 text-xs font-medium rounded-md border ${offer.status === "active"
                                ? "bg-green-500/20 text-green-400 border-green-500/30"
                                : offer.status === "expired"
                                  ? "bg-yellow-500/20 text-yellow-400 border-yellow-500/30"
                                  : offer.status === "scheduled"
                                    ? "bg-blue-500/20 text-blue-400 border-blue-500/30"
                                    : "bg-gray-500/20 text-gray-400 border-gray-500/30"
                              }`}
                          >
                            {offer.status}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-sm text-right space-x-2">
                          <button
                            onClick={() => handleViewClick(offer)}
                            className="text-muted-foreground hover:text-foreground"
                            title="View Offer"
                          >
                            <Eye size={16} />
                          </button>
                          <button
                            onClick={() => handleEditClick(offer)}
                            className="text-muted-foreground hover:text-foreground"
                            title="Edit Offer"
                          >
                            <Edit size={16} />
                          </button>
                          <button
                            onClick={() => handleDeleteClick(offer)}
                            className='text-muted-foreground hover:text-foreground'
                            title='Delete Offer'
                          >
                            <Trash2 size={16} />
                          </button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {data?.pagination && (
              <div className="flex flex-col sm:flex-row items-center justify-between px-4 py-3 border-t border-border space-y-3 sm:space-y-0">
                <div className="text-sm text-muted-foreground">
                  Showing{" "}
                  {(data.pagination.currentPage - 1) *
                    data.pagination.itemsPerPage +
                    1}{" "}
                  to{" "}
                  {Math.min(
                    data.pagination.currentPage * data.pagination.itemsPerPage,
                    data.pagination.totalItems,
                  )}{" "}
                  of {data.pagination.totalItems} offers
                </div>

                <div className="flex items-center space-x-2">
                  <button
                    onClick={() =>
                      updateFilter(
                        "page",
                        String(data.pagination.currentPage - 1),
                      )
                    }
                    disabled={data.pagination.currentPage === 1}
                    className="p-2 border border-border rounded-md bg-card text-muted-foreground hover:bg-accent hover:text-foreground disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    <ChevronLeft size={16} />
                  </button>

                  <div className="flex items-center space-x-1">
                    {[...Array(data.pagination.totalPages)].map((_, idx) => {
                      const page = idx + 1;
                      const current = data.pagination.currentPage;

                      // Show first, last, current, and adjacent pages
                      if (
                        page === 1 ||
                        page === data.pagination.totalPages ||
                        (page >= current - 1 && page <= current + 1)
                      ) {
                        return (
                          <button
                            key={page}
                            onClick={() => updateFilter("page", String(page))}
                            className={`min-w-[2rem] px-3 py-1 text-sm rounded-md transition-colors ${
                              page === current
                                ? "bg-primary text-primary-foreground font-medium"
                                : "bg-card text-muted-foreground hover:bg-accent hover:text-foreground border border-border"
                            }`}
                          >
                            {page}
                          </button>
                        );
                      } else if (page === current - 2 || page === current + 2) {
                        return (
                          <span key={page} className="text-muted-foreground">
                            ...
                          </span>
                        );
                      }
                      return null;
                    })}
                  </div>

                  <button
                    onClick={() =>
                      updateFilter(
                        "page",
                        String(data.pagination.currentPage + 1),
                      )
                    }
                    disabled={
                      data.pagination.currentPage === data.pagination.totalPages
                    }
                    className="p-2 border border-border rounded-md bg-card text-muted-foreground hover:bg-accent hover:text-foreground disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    <ChevronRight size={16} />
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
        {/* Modals */}
        {isEditModalOpen && (
          <EditOfferModal
            isEditModalOpen={isEditModalOpen}
            setIsEditModalOpen={setIsEditModalOpen}
            offer={selectedOffer}
          />
        )}
        <ViewOfferModal
          isOpen={isViewModalOpen}
          onClose={() => {
            setIsViewModalOpen(false);
            setSelectedOffer(null);
          }}
          offer={selectedOffer}
        />
        <DeleteOfferModal
          isDeleteModalOpen={isDeleteModalOpen}
          setIsDeleteModalOpen={(isOpen) => {
            setIsDeleteModalOpen(isOpen);
            if (!isOpen) setSelectedOffer(null);
          }}
          offer={selectedOffer}
        />
      </div>
    </Layout>
  );
};

export default Offer;
